import {
  provideRouter,
  Routes,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig
} from '@angular/router';
import {
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import { provideEventPlugins } from '@taiga-ui/event-plugins';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideHttpCache, withHttpCacheInterceptor } from '@ngneat/cashew';
import { httpErrorInterceptor } from './error/http-error.interceptor';
import { authInterceptor } from './auth/interceptors/auth.interceptor';
import { AuthStartupFacade } from './auth/facades/auth-startup.facade';
import { provideAnalytics } from './analytics/providers/analytics.provider';
import { provideYandexMetrika } from './analytics/strategies/yandex-metrika/yandex-metrika.provider';
import { environment } from '../../environments/environment';
import { provideAnalyticsRouterTracking } from './analytics/providers/analytics-router-tracking.provider';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

export interface CoreOptions {
  routes: Routes;
}

export function provideCore({ routes }: CoreOptions) {
  return [
    provideAnimationsAsync(),
    // reasonable default for most applications
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withComponentInputBinding(),
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled'
      })
    ),
    // other 3rd party libraries providers like NgRx, provideStore()

    // other application-specific providers and setup
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    provideHttpClient(
      withInterceptors([withHttpCacheInterceptor(), httpErrorInterceptor, authInterceptor])
    ),
    provideHttpCache(),
    provideEventPlugins(),
    provideAnalytics([
      provideYandexMetrika(environment.yandexMetrikaId),
      provideAnalyticsRouterTracking()
    ]),
    // perform initialization, has to be last
    provideAppInitializer(() => {
      const authStartupFacade = inject(AuthStartupFacade);
      return authStartupFacade.restoreState();
    })
  ];
}
